/**
 * Floe SDK Logger
 * Wraps console methods so that log/warn output is suppressed unless debug mode is enabled.
 * Errors are always surfaced regardless of the debug flag.
 *
 * Usage:
 *   import { createLogger, Logger } from './logger';
 *
 *   // Build an instance-scoped logger at construction time:
 *   const log = createLogger(config.debug ?? false);
 *
 *   // Use everywhere instead of console.*:
 *   log.log('[MyModule] something happened');
 *   log.warn('[MyModule] something looks off');
 *   log.error('[MyModule] something broke', err);
 *
 *   // Cheap guard for expensive-to-build debug payloads:
 *   if (log.isDebug) log.log('[MyModule]', expensiveSerialize(value));
 */

export interface Logger {
  log(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  /** Always emits regardless of the debug flag. */
  error(...args: unknown[]): void;
  /** True when debug logging is enabled. Use as a cheap guard before expensive formatting. */
  readonly isDebug: boolean;
}

export function createLogger(debug: boolean): Logger {
  return {
    get isDebug() { return debug; },
    log: (...args: unknown[]): void => {
      if (debug) console.log(...args);
    },
    warn: (...args: unknown[]): void => {
      if (debug) console.warn(...args);
    },
    // Errors are always shown - they indicate real failures that need attention.
    error: (...args: unknown[]): void => {
      console.error(...args);
    },
  };
}

/**
 * Module-level default logger (debug always off).
 * Used by static methods and module-level functions that cannot receive an injected logger.
 * Class instances should use createLogger() and store their own Logger.
 */
export const logger = createLogger(false);
